import { useEffect } from 'react';
import { useToastStore, type AlertToast } from '@/stores/toast-store';
import { AlertTriangle, UserCheck, X } from 'lucide-react';

const AUTO_DISMISS_MS = 7000;

function ToastItem({ toast, onDismiss }: { toast: AlertToast; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const isNew = toast.kind === 'new_alert';

  return (
    <div
      className={`flex items-start gap-3 w-80 rounded-lg shadow-xl border px-4 py-3 animate-slide-in-right ${
        isNew
          ? 'bg-red-900/90 border-red-500/60 text-white'
          : 'bg-slate-800/95 border-blue-500/50 text-white'
      }`}
    >
      <div
        className={`mt-0.5 shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isNew ? 'bg-red-500/30' : 'bg-blue-500/30'
        }`}
      >
        {isNew ? (
          <AlertTriangle className={`w-4 h-4 ${isNew ? 'text-red-300 animate-pulse' : 'text-blue-300'}`} />
        ) : (
          <UserCheck className="w-4 h-4 text-blue-300" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">{toast.title}</p>
        <p className="text-xs mt-0.5 text-white/70 truncate">{toast.body}</p>
      </div>

      <button
        onClick={onDismiss}
        className="shrink-0 mt-0.5 text-white/40 hover:text-white/80 transition-colors"
        aria-label="Cerrar"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function AlertToastContainer() {
  const { toasts, dismiss } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem toast={t} onDismiss={() => dismiss(t.id)} />
        </div>
      ))}
    </div>
  );
}
